import type { FieldTypes } from "@/block-system/blocks/Form/Field/types";
import type { APIFieldTypeLatest } from "@/server/services/tables/types/tables-latest";
import { BlockType } from "@prisma/client";

export const blockType = "field-block";
export const blockTypeDbName = BlockType.Field;

export const blockTypeToName = {
  [blockType]: blockTypeDbName,
} as const;

export const blockNameToType = {
  [blockTypeDbName]: blockType,
} as const;

/**
 * Imagine you want to change a `yes-no` field block to a `checkbox` field block.
 * Since the underlying Zapier Table field type is `boolean`, we can safely convert the field type.
 *
 * Now, imagine you want to change a `yes-no` field block to a `number` field block.
 * For that conversion to happen, we need to change the underlying Zapier Table field type to `number`.
 * This is a destructive conversion.
 *
 * This constant tells us which conversions are safe.
 * If the conversion is not listed in this constant, it is a destructive conversion.
 */
export const tableFieldTypeToFieldBlockSafeConversions: Record<
  string,
  FieldTypes[]
> = {
  string: ["text"],
  labeled_string: ["dropdown"],
  text: ["textarea"],
  boolean: ["checkbox", "yes-no"],
  currency: ["currency"],
  datetime: ["date-picker"],
  email: ["email"],
  decimal: ["number"],
  number: ["number"],
  phone_number: ["phone-number"],
  link: ["url", "file-upload"],
  multiple_link: ["multi-url", "multiple-file-upload"],
  linked_record: ["linked-record"],
};

/**
 * This constant tells us what kinds of Zapier Table field types are supported by a given Field Block.
 */
export const fieldBlockTypeToTableFieldTypes = {
  text: ["string"],
  dropdown: ["labeled_string"],
  textarea: ["text"],
  checkbox: ["boolean"],
  "yes-no": ["boolean"],
  currency: ["currency"],
  "date-picker": ["datetime"],
  email: ["email"],
  number: ["decimal", "number"],
  "phone-number": ["phone_number"],
  url: ["link"],
  "file-upload": ["link"],
  "multi-url": ["multiple_link"],
  "multiple-file-upload": ["multiple_link"],
  "linked-record": ["linked_record"],
  formula: ["formula"],
  "ai-formula": ["ai_formula"],
  "multiple-linked-record": ["multiple_linked_record"],
} satisfies Record<FieldTypes, APIFieldTypeLatest[]>;
